"use client"

import { useEffect, useState } from "react"
import { useAuthStore } from "@/lib/auth-store"
import { useFeatureFlag } from "@/lib/feature-flags"
import { fetchSeenState, markSeen } from "@/lib/client/seen-state"
import { useModalSlot } from "@/lib/client/modal-coordinator"
import { DEFAULT_GLYMTOBER_CONFIG, isGlymtoberActiveWithConfig, type GlymtoberConfig } from "@/lib/glymtober"
import { useSearchParams } from "next/navigation"
import { EarlyAccessCard, EARLY_ACCESS_SEEN_KEY, LOCAL_EARLY_ACCESS_SEEN_KEY } from "./early-access-card"
import { GlymtoberCard, glymtoberSeenKeyForYear, localGlymtoberSeenKeyForYear } from "./glymtober-card"
import { HolidayCard } from "./holiday-card"
import { DEFAULT_HOLIDAY_EVENTS, activeHoliday, holidaySeenKey, localHolidaySeenKey, mergeHolidayConfig, type HolidayEvent } from "@/lib/holiday-events"
import { FIRST_SEEN_KEY, LOCAL_FIRST_SEEN_DATE_KEY } from "./early-access-modal"

const DELAY_MS = 1200
// Fade+blur one card out, swap its content while still hidden, then fade+blur
// the next one in — same total duration on both ends so the swap reads as one
// continuous motion instead of an instant cut.
const TRANSITION_MS = 220

type CardId = "early_access" | "glymtober" | "holiday"

/** One shared popup frame for the day-one promo cards (Early Access, and
 * Glymtober during its admin-configured window, default October — see
 * lib/glymtober.ts) instead of two independently-timed modals racing each
 * other — reported live as way too many popups when Early Access, Glymtober,
 * and Patch Notes could all fire on the very first visit. Shows one card at
 * a time; finishing a card marks that card's own seen-state and advances to
 * the next eligible one, closing once the list is exhausted. Founder is NOT
 * part of this stack — it already has its own working day-2+ gate (see
 * founder-promo-modal.tsx) and is unrelated. */
export function DayOnePromoStack() {
  const { user, isLoading: authLoading } = useAuthStore()
  const { enabled: earlyAccessEnabled, loading: earlyAccessFlagLoading } = useFeatureFlag("early_access_modal_enabled")
  const { enabled: glymtoberEnabled, loading: glymtoberFlagLoading } = useFeatureFlag("glymtober_modal_enabled")
  // Holiday events (Glymsgiving, Glymus, New Year's: lib/holiday-events.ts). Its own flag, OFF by default, so no holiday popup shows until the owner turns it on.
  const { enabled: holidayEnabled, loading: holidayFlagLoading } = useFeatureFlag("holiday_modal_enabled")
  const [holiday, setHoliday] = useState<{ event: HolidayEvent; date: Date } | null>(null)
  const searchParams = useSearchParams()
  const [cards, setCards] = useState<CardId[] | null>(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const [visible, setVisible] = useState(false)
  // "idle" = settled and visible; "leaving" = fading/blurring out ahead of a
  // swap or close. Drives the CSS transition below — see advance().
  const [phase, setPhase] = useState<"idle" | "leaving">("idle")

  // Dev-only bypass: ?preview=early-access or ?preview=glymtober shows just
  // that one card instantly; ?preview=day-stack forces BOTH regardless of
  // real flags/dates/seen-state, for testing the actual sequencing.
  const previewParam = process.env.NODE_ENV !== "production" ? searchParams.get("preview") : null
  // Dev-only: ?preview=glymsgiving | glymus | new-years, with an optional ?previewDate=12-25 to see the picture for that day.
  const previewDateParam = process.env.NODE_ENV !== "production" ? searchParams.get("previewDate") : null

  useEffect(() => {
    if (previewParam === "early-access") { setCards(["early_access"]); setVisible(true); return }
    if (previewParam === "glymtober") { setCards(["glymtober"]); setVisible(true); return }
    if (previewParam === "day-stack") { setCards(["early_access", "glymtober"]); setVisible(true); return }
    const holidayPreviewId = previewParam === "new-years" ? "new_years" : previewParam === "glymus" || previewParam === "glymsgiving" ? previewParam : null
    if (holidayPreviewId) {
      const event = DEFAULT_HOLIDAY_EVENTS.find((e) => e.id === holidayPreviewId)!
      const m = /^([0-9]{1,2})-([0-9]{1,2})$/.exec(previewDateParam ?? "")
      const now = new Date()
      setHoliday({ event, date: m ? new Date(now.getFullYear(), parseInt(m[1]) - 1, parseInt(m[2]), 12) : now })
      setCards(["holiday"]); setVisible(true); return
    }
    if (earlyAccessFlagLoading || glymtoberFlagLoading || holidayFlagLoading || authLoading) return

    let cancelled = false
    const run = async () => {
      // Admin-configured window (default: October 1-31, see
      // /api/admin/glymtober-config + components/admin/glymtober-schedule-editor.tsx).
      // Falls back to that same default on any fetch error.
      const glymtoberConfig: GlymtoberConfig = await fetch("/api/admin/glymtober-config")
        .then((r) => (r.ok ? r.json() : DEFAULT_GLYMTOBER_CONFIG))
        .catch(() => DEFAULT_GLYMTOBER_CONFIG)
      if (cancelled) return

      let seenKeys: Set<string>
      if (user) {
        const { keys } = await fetchSeenState()
        if (cancelled) return
        seenKeys = keys
        // First-visit marker Founder's day-2+ gate depends on — previously
        // set unconditionally by EarlyAccessModal regardless of whether
        // Early Access itself was eligible, so it still has to happen here
        // even when Early Access isn't one of today's eligible cards.
        if (!seenKeys.has(FIRST_SEEN_KEY)) void markSeen(FIRST_SEEN_KEY)
      } else {
        seenKeys = new Set()
        try {
          if (!localStorage.getItem(LOCAL_FIRST_SEEN_DATE_KEY)) {
            localStorage.setItem(LOCAL_FIRST_SEEN_DATE_KEY, new Date().toISOString().slice(0, 10))
          }
        } catch {}
      }
      if (cancelled) return

      const isSeen = (accountKey: string, localKey: string) =>
        user ? seenKeys.has(accountKey) : (() => { try { return localStorage.getItem(localKey) === "1" } catch { return false } })()

      const eligible: CardId[] = []
      if (earlyAccessEnabled && !isSeen(EARLY_ACCESS_SEEN_KEY, LOCAL_EARLY_ACCESS_SEEN_KEY)) {
        eligible.push("early_access")
      }
      const year = new Date().getFullYear()
      if (glymtoberEnabled && isGlymtoberActiveWithConfig(glymtoberConfig) && !isSeen(glymtoberSeenKeyForYear(year), localGlymtoberSeenKeyForYear(year))) {
        eligible.push("glymtober")
      }
      if (holidayEnabled) {
        const holidayConfig = await fetch("/api/admin/holiday-config")
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null)
        if (cancelled) return
        const events = holidayConfig ? mergeHolidayConfig(holidayConfig) : DEFAULT_HOLIDAY_EVENTS
        const today = new Date()
        const active = activeHoliday(events, today)
        if (active && !isSeen(holidaySeenKey(active, today), localHolidaySeenKey(active, today))) {
          setHoliday({ event: active, date: today })
          eligible.push("holiday")
        }
      }

      if (eligible.length === 0) { setCards([]); return }
      setCards(eligible)
      setTimeout(() => { if (!cancelled) setVisible(true) }, DELAY_MS)
    }
    void run()
    return () => { cancelled = true }
  }, [previewParam, previewDateParam, earlyAccessFlagLoading, glymtoberFlagLoading, holidayFlagLoading, authLoading, user, earlyAccessEnabled, glymtoberEnabled, holidayEnabled])

  const advance = () => {
    setPhase("leaving")
    setTimeout(() => {
      setActiveIndex((i) => {
        const next = i + 1
        if (!cards || next >= cards.length) setVisible(false)
        return next
      })
      // Keep it hidden for one more frame so the NEW card also mounts
      // blurred/transparent, then flip back to "idle" so its own entrance
      // animates in via the same transition classes below — a fade-out
      // immediately followed by a fade-in, instead of an instant swap.
      requestAnimationFrame(() => requestAnimationFrame(() => setPhase("idle")))
    }, TRANSITION_MS)
  }

  // The popup stays invisible until the card's hero art has loaded, so nobody sees a blank white card first.
  // A timer shows it anyway if the art is very slow, so it can never stay hidden forever.
  const [artReady, setArtReady] = useState(false)
  useEffect(() => {
    if (!visible) return
    setArtReady(false)
    const t = setTimeout(() => setArtReady(true), 4000)
    return () => clearTimeout(t)
  }, [visible, activeIndex])

  const stackOpen = visible && !!cards && activeIndex < cards.length
  const canShow = useModalSlot("dayOnePromos", stackOpen, { bypass: !!previewParam })

  if (!visible || !cards || activeIndex >= cards.length || !canShow) return null

  const currentId = cards[activeIndex]

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm overflow-y-auto transition-opacity duration-300"
      style={{ opacity: artReady ? 1 : 0, pointerEvents: artReady ? undefined : "none" }}
    >
      <div
        className="relative w-full max-w-sm transition-[opacity,filter] ease-out"
        style={{
          transitionDuration: `${TRANSITION_MS}ms`,
          opacity: phase === "leaving" ? 0 : 1,
          filter: phase === "leaving" ? "blur(10px)" : "blur(0px)",
        }}
      >
        {currentId === "early_access" && <EarlyAccessCard onDone={advance} onReady={() => setArtReady(true)} />}
        {currentId === "glymtober" && <GlymtoberCard onDone={advance} onReady={() => setArtReady(true)} />}
        {currentId === "holiday" && holiday && <HolidayCard event={holiday.event} date={holiday.date} onDone={advance} onReady={() => setArtReady(true)} />}
      </div>
    </div>
  )
}
